import React, { Component, Context, ContextType } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema, RegistryFieldsType, StrictRJSFSchema, TemplatesType, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import type { JSONSchema7Definition } from 'json-schema';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { AuthContext, type AuthContextValue } from '~/auth';

import { Alert, Box } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';
import {
  REGIONS_COUNTRY_CODES,
  isPhoneNumberValidForRegion,
} from '@luxuryescapes/lib-regions/lib/phoneNumberValidation';

import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import type { LanguageOption } from '~/services/LabService';
import UsersService from '~/services/UsersService';

import {
  BusinessDetailsForm,
  apiToFormBusinessDetails,
  formToApiBusinessDetails,
} from '~/utils/businessDetailsMapping';

import BusinessAddressField from './BusinessAddressWidget';
import GridObjectFieldTemplate from './Forms/GridObjectFieldTemplate';
import LinkedAccountField from './Forms/LinkedAccountField';
import SimpleArrayFieldItemTemplate from './Forms/SimpleArrayFieldItemTemplate';
import SimpleArrayFieldTemplate from './Forms/SimpleArrayFieldTemplate';

const defaultFieldsOrder = [
  'customer_support_code',
  'title',
  'givenName',
  'surname',
  'dob',
  'email',
  'email_verified',
  'phone_prefix',
  'phone',
  'shadow_ban_user',
  'country_code',
  'postcode',
  'language',
  'recently_used_airport_code',
  'resetPassword',
  'password',
  '*',
  'business_details',
  'partnerships',
];

const DEFAULT_PHONE_FORMAT_HELP_MESSAGE = 'local number only, eg 402123123. No leading 0, no +61, no prefixes.';

const initUiSchema: UiSchema = {
  // Enable 3-column layout (12-col grid, default span = 4 = 33%)
  'ui:options': { gridLayout: true, gridColumns: 3 },
  'ui:order': defaultFieldsOrder,
  // Row 1
  customer_support_code: { 'ui:colSpan': 2, 'ui:readonly': true },
  title: { 'ui:widget': 'select', 'ui:colSpan': 2 },
  // Row 2
  dob: { 'ui:widget': 'date' },
  email: { 'ui:widget': 'email' },
  email_verified: { 'ui:readonly': true },
  // Row 3
  phone: {
    'ui:help': DEFAULT_PHONE_FORMAT_HELP_MESSAGE,
  },
  // Location fields
  country_code: { 'ui:colSpan': 2 },
  postcode: { 'ui:colSpan': 2 },
  language: { 'ui:widget': 'select', 'ui:colSpan': 2 },
  recently_used_airport_code: { 'ui:emptyValue': null, 'ui:colSpan': 2 },
  resetPassword: { 'ui:colSpan': 2 },
  roles: { 'ui:colSpan': 6 },
  vendors: { 'ui:colSpan': 6 },
  partnerships: {
    'ui:colSpan': 12,
    // Enable grid layout on each individual partnership so their fields are 1/3 width
    kfp: { 'ui:options': { gridLayout: true, gridColumns: 3 } },
    vff: { 'ui:options': { gridLayout: true, gridColumns: 3 } },
  },
};

const FORM_TEMPLATES: Partial<TemplatesType> = {
  ObjectFieldTemplate: GridObjectFieldTemplate,
  ArrayFieldTemplate: SimpleArrayFieldTemplate,
  ArrayFieldItemTemplate: SimpleArrayFieldItemTemplate,
};

const PHONE_FORMAT_HELP_MESSAGE_BY_REGION = {
  AU: 'AU local number only, e.g. 412345678. No leading 0, no +61, no prefixes.',
  GB: 'GB local number only, e.g. 7123456789. No leading 0, no +44, no prefixes.',
  US: 'US local number only, e.g. 4151234567. No leading 0, no +1, no prefixes.',
  NZ: 'NZ local number only, e.g. 212345678. No leading 0, no +64, no prefixes.',
  SG: 'Singapore local number only, eg. 8021231234. No leading 0, no +65, no prefixes.',
};

const EMPTY_LANGUAGE_OPTION: LanguageOption = { value: '', label: 'Not set' };

function getLanguageOptions(languageOptions: Array<LanguageOption>, currentLanguage?: string | null) {
  const options = [EMPTY_LANGUAGE_OPTION, ...languageOptions];
  if (!currentLanguage || options.some(({ value }) => value === currentLanguage)) {
    return options;
  }

  return [...options, { value: currentLanguage, label: currentLanguage }];
}

const customFields: RegistryFieldsType = {
  BusinessAddressField,
  LinkedAccountField,
};

type Props = {
  user: App.User;
  schema: RJSFSchema;
  brand: App.Brands;
  update?: (user: App.User) => void;
  initialBusinessDetails?: BusinessDetailsForm;
  onBusinessDetailsSaved?: () => void;
  languageOptions?: Array<LanguageOption>;
  linkedAccountEmail?: string;
  linkedAccountId?: string;
  disallowedRoles?: Array<{
    role: string;
    message: string;
  }>;
};

type FormData = Omit<App.User, 'gdpr'> & {
  resetPassword?: boolean;
  password?: string;
  vendors?: Array<string>;
  gdpr: boolean | string;
  business_details?: BusinessDetailsForm;
  linked_personal_account?: string;
};

type State = {
  formData: FormData;
  schema: RJSFSchema;
  alertSuccess: boolean;
  alertError: boolean;
  errorMessage: string;
  partnershipsAlertError: boolean;
  partnershipsErrorMessage: string;
  flightEnabled: boolean;
  referralProgramEnabled: boolean;
  shadowBanUser: boolean;
};

export default class UpdateUser extends Component<Props, State> {
  static contextType: Context<AuthContextValue> = AuthContext;
  context: ContextType<typeof AuthContext>;

  submitButton: HTMLButtonElement;

  constructor(props) {
    super(props);

    const formData: FormData = cloneDeep(this.props.user);
    const schema = cloneDeep(this.props.schema);
    formData.language = formData.language ?? '';

    const mergedRegions = ['US', 'CA'];
    const preferredRegions = ['AU', 'NZ', 'GB', 'SG'];
    const sortedRegionCodes = [
      ...preferredRegions,
      ...Object.keys(REGIONS_COUNTRY_CODES)
        .filter((code) => !preferredRegions.includes(code) && !mergedRegions.includes(code))
        .sort((a, b) => REGIONS_COUNTRY_CODES[a].countryName.localeCompare(REGIONS_COUNTRY_CODES[b].countryName)),
    ];

    const phonePrefixSchemas: Array<JSONSchema7Definition> = sortedRegionCodes.map((regionCode) => {
      const region = REGIONS_COUNTRY_CODES[regionCode];
      const internationalPhonePrefix = region.countryCode;
      const regionName = region.countryName;

      return {
        type: 'string',
        title: `${regionName} - ${regionCode} (+${internationalPhonePrefix})`,
        enum: [internationalPhonePrefix],
      };
    });

    phonePrefixSchemas.splice(2, 0, {
      type: 'string',
      title: 'United States / Canada - US/CA (+1)',
      enum: ['1'],
    });

    schema.properties.phone_prefix = {
      ...(schema.properties.phone_prefix as object),
      oneOf: phonePrefixSchemas,
    };

    // Set default phone prefix to AU if unknown phone_prefix
    if (formData.phone_prefix) {
      const phonePrefixRegion = Object.keys(REGIONS_COUNTRY_CODES).find(
        (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === formData.phone_prefix,
      );

      if (!phonePrefixRegion) {
        formData.phone_prefix = '61';
      }
    }

    this.state = {
      schema,
      formData,
      alertSuccess: false,
      alertError: !formData.country_code,
      errorMessage: !formData.country_code ? 'Country is a required field!' : '',
      partnershipsAlertError: false,
      partnershipsErrorMessage: '',
      flightEnabled: formData.flights_enabled,
      referralProgramEnabled: formData.referral_program_enabled,
      shadowBanUser: formData.shadow_ban_user,
    };

    this.customValidate = this.customValidate.bind(this);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.initialBusinessDetails !== this.props.initialBusinessDetails) {
      const shouldSyncBusinessDetails =
        this.props.initialBusinessDetails && this.isBusinessDetailsEmpty(this.state.formData.business_details);
      if (shouldSyncBusinessDetails) {
        this.setState((prevState) => ({
          formData: {
            ...prevState.formData,
            business_details: this.props.initialBusinessDetails,
          },
        }));
      }
    }
  }

  isBusinessDetailsEmpty(details?: BusinessDetailsForm) {
    if (!details) {
      return true;
    }
    const { abn, businessName, businessPosition, businessAddress } = details;
    const hasAddressValue =
      businessAddress && Object.values(businessAddress).some((value) => value !== null && value !== '');
    return !abn && !businessName && !businessPosition && !hasAddressValue;
  }

  getDisallowedRoleMessage(roles: Array<string>) {
    return this.props.disallowedRoles?.find(({ role }) => roles.includes(role))?.message;
  }

  filterDisallowedRoleOptions(schema: RJSFSchema, selectedRoles: Array<string>) {
    const disallowedRoles = (this.props.disallowedRoles ?? [])
      .map(({ role }) => role)
      .filter((role) => !selectedRoles.includes(role));

    if (disallowedRoles.length === 0) {
      return;
    }

    const rolesSchema = schema.properties.roles as StrictRJSFSchema;
    const roleItemsSchema = rolesSchema?.items as StrictRJSFSchema;
    const roleItemsSchemaWithEnumNames = roleItemsSchema as StrictRJSFSchema & { enumNames?: Array<string> };
    const roleEnums = roleItemsSchema?.enum;

    if (!Array.isArray(roleEnums)) {
      return;
    }

    const enumNames = roleItemsSchemaWithEnumNames.enumNames;
    const filteredRoleEnums = [];
    const filteredRoleEnumNames = [];

    roleEnums.forEach((role, index) => {
      if (typeof role === 'string' && disallowedRoles.includes(role)) {
        return;
      }

      filteredRoleEnums.push(role);
      if (Array.isArray(enumNames)) {
        filteredRoleEnumNames.push(enumNames[index]);
      }
    });

    roleItemsSchema.enum = filteredRoleEnums;
    if (Array.isArray(enumNames)) {
      roleItemsSchemaWithEnumNames.enumNames = filteredRoleEnumNames;
    }
  }

  customValidate(formData, errors, uiSchema) {
    if (formData.phone_prefix) {
      const phonePrefix = formData.phone_prefix.split('_')[0];
      const regionCode = Object.keys(REGIONS_COUNTRY_CODES).find(
        (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === phonePrefix,
      );

      const isPhoneNumberValid = isPhoneNumberValidForRegion({
        phoneNumber: formData.phone,
        regionCode,
      });

      if (!isPhoneNumberValid) {
        errors.phone.addError('Phone number is not valid for the selected region/phone prefix');
      }
    }

    const roles = Array.isArray(formData.roles) ? formData.roles : [];
    const disallowedRoleMessage = this.getDisallowedRoleMessage(roles);
    if (disallowedRoleMessage) {
      errors.roles.addError(disallowedRoleMessage);
    }

    return errors;
  }

  getRegionCodeFromPhonePrefix(phonePrefix?: string | null) {
    if (!phonePrefix) {
      return undefined;
    }
    const phonePrefixParts = phonePrefix.split('_');
    let selectedRegionCode;

    if (phonePrefixParts.length > 1) {
      // e.g. in the format 1_US or 1_CA, we want to get the 'US' or 'CA'
      selectedRegionCode = phonePrefixParts[1];
    } else {
      const selectedRegion = Object.keys(REGIONS_COUNTRY_CODES).find(
        (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === phonePrefixParts[0],
      );

      selectedRegionCode = selectedRegion;
    }

    return selectedRegionCode;
  }

  handleStateData = () => {
    const schema = cloneDeep(this.state.schema);
    const formData = { ...this.state.formData };
    if (this.context.user.roles.includes(ROLE_ICS_STORE_TEAM)) {
      delete schema.properties.shadow_ban_user;
    }

    const roles = Array.isArray(formData.roles) ? formData.roles : [];
    if (!Array.isArray(formData.roles)) {
      formData.roles = roles;
    }

    this.filterDisallowedRoleOptions(schema, roles);
    const rolesEnumNames = (schema.properties.roles as StrictRJSFSchema)?.items?.['enumNames'];

    const businessDetailsSchema: StrictRJSFSchema = {
      type: 'object',
      title: 'Business details',
      properties: {
        abn: { type: ['string', 'null'], title: 'ABN', maxLength: 255 },
        businessName: { type: ['string', 'null'], title: 'Business name', maxLength: 255 },
        businessPosition: {
          type: ['string', 'null'],
          title: 'Position in business',
          maxLength: 255,
        },
        businessAddress: {
          type: ['object', 'null'],
          title: 'Business address',
          properties: {
            unit_number: { type: ['string', 'null'] },
            street: { type: ['string', 'null'] },
            city: { type: ['string', 'null'] },
            state: { type: ['string', 'null'] },
            postcode: { type: ['string', 'null'] },
            country: { type: ['string', 'null'] },
            formatted_address: { type: ['string', 'null'] },
          },
        },
      },
    };

    const languageOptions = getLanguageOptions(this.props.languageOptions ?? [], formData.language);

    //reset password
    schema.properties = {
      ...schema.properties,
      title: {
        type: 'string',
        title: 'Title',
        enum: ['Mr', 'Ms', 'Mrs', 'Miss', 'Master'],
        default: 'Mr',
      },
      email_verified: { type: 'boolean', title: 'Email Verified' },
      resetPassword: {
        type: 'boolean',
        title: 'Reset Password',
      },
      customer_support_code: {
        type: 'string',
        title: 'Customer Support Code',
      },
      roles: {
        ...(schema.properties.roles as object),
        items: {
          ...((schema.properties.roles as StrictRJSFSchema).items as object),
          title: 'Role',
        },
      },
      recently_used_airport_code: {
        ...(schema.properties.recently_used_airport_code as object),
        type: ['string', 'null'],
      },
      postcode: {
        ...(schema.properties.postcode as object),
        type: ['string', 'null'],
      },
      phone_prefix: {
        ...(schema.properties.phone_prefix as object),
        type: ['string', 'null'],
      },
      language: {
        type: 'string',
        title: 'Language',
        oneOf: languageOptions.map(({ value, label }) => ({
          type: 'string',
          title: label,
          enum: [value],
        })),
      },
      business_details: businessDetailsSchema,
    };

    const uiSchema: UiSchema = {
      ...initUiSchema,
      roles: {
        ...(initUiSchema.roles as object),
        items: {
          'ui:enumNames': rolesEnumNames,
        },
      },
    };

    delete schema.properties.password;

    // reset password
    if (formData.resetPassword) {
      schema.properties = {
        ...schema.properties,
        password: { type: 'string', title: 'Password' },
      };
    } else {
      delete formData.password;
      delete schema.properties.password;
    }

    uiSchema.business_details = {
      'ui:colSpan': 12,
      'ui:options': { gridLayout: true },
      'ui:order': ['abn', 'businessName', 'businessPosition', 'businessAddress'],
      abn: { 'ui:colSpan': 3 },
      businessName: { 'ui:colSpan': 3 },
      businessPosition: { 'ui:colSpan': 3 },
      businessAddress: { 'ui:field': 'BusinessAddressField', 'ui:colSpan': 12 },
    };

    if (!formData.business_details) {
      const initialBusinessDetails = this.props.initialBusinessDetails;
      formData.business_details = {
        abn: initialBusinessDetails?.abn ?? null,
        businessName: initialBusinessDetails?.businessName ?? null,
        businessPosition: initialBusinessDetails?.businessPosition ?? null,
        businessAddress: initialBusinessDetails?.businessAddress ?? null,
      };
    }

    delete formData.custom_claims;
    delete schema.properties.custom_claims;

    //partnerships
    if (window.configs.KRIS_FLYER_ENABLED !== 'true') {
      delete (schema.properties.partnerships as StrictRJSFSchema).properties.kfp;
    }

    if (window.configs.QANTAS_EARN_ORDER_BASED_ENABLED === 'true') {
      delete (schema.properties.partnerships as StrictRJSFSchema).properties.qff;
    }

    //vendor
    if (!roles.includes('vendor-user')) {
      schema.properties = { ...schema.properties };
      if (formData.vendors) {
        delete formData.vendors;
      }
      if (schema.properties.vendors) {
        delete schema.properties.vendors;
      }
    } else {
      schema.properties = {
        ...schema.properties,
        vendors: {
          type: 'array',
          title: 'Vendor ID',
          items: { type: 'string' },
        },
      };
    }

    if (roles.length === 0 && formData.status === 'ENABLED') {
      formData.roles = ['customer'];
    }

    if (formData.gdpr) {
      formData.gdpr = true;
    } else {
      delete formData.gdpr;
    }

    if (!formData.phone) {
      delete formData.phone;
    }

    if (!formData.country_code) {
      delete formData.country_code;
    }

    if (!formData.dob) {
      delete formData.dob;
    }

    if (this.context.user.roles.includes(ROLE_ICS_STORE_TEAM)) {
      uiSchema.dob = { ...uiSchema.dob, 'ui:widget': 'hidden' };
    }

    uiSchema.banned_led_user = { 'ui:widget': 'hidden' };

    const selectedRegionCode = this.getRegionCodeFromPhonePrefix(formData.phone_prefix);
    const phoneNumberHelpMessage =
      PHONE_FORMAT_HELP_MESSAGE_BY_REGION[selectedRegionCode] || DEFAULT_PHONE_FORMAT_HELP_MESSAGE;
    uiSchema.phone = {
      ...uiSchema.phone,
      'ui:help': phoneNumberHelpMessage,
    };

    if (this.props.linkedAccountEmail !== undefined) {
      schema.properties = {
        ...schema.properties,
        linked_personal_account: { type: 'string', title: 'Linked personal account' },
      };
      formData.linked_personal_account = this.props.linkedAccountEmail;
      const existingOrder = uiSchema['ui:order'] || Object.keys(schema.properties);
      const phoneIndex = existingOrder.indexOf('phone');
      const insertAt = phoneIndex >= 0 ? phoneIndex + 1 : existingOrder.length;
      uiSchema['ui:order'] = [
        ...existingOrder.slice(0, insertAt),
        'linked_personal_account',
        ...existingOrder.slice(insertAt),
      ];
      uiSchema.linked_personal_account = {
        'ui:field': 'LinkedAccountField',
        'ui:options': { creditCustomerId: this.props.linkedAccountId },
      };
    }

    return { schema, formData, uiSchema };
  };

  handleChange = (data: IChangeEvent<FormData>) => {
    const formData = data.formData;

    this.setState({
      formData,
      alertSuccess: false,
      alertError: false,
      errorMessage: '',
      partnershipsAlertError: false,
      partnershipsErrorMessage: '',
      flightEnabled: formData.flights_enabled,
      referralProgramEnabled: formData.referral_program_enabled,
      shadowBanUser: formData.shadow_ban_user,
    });
  };

  handleSubmit = async (event: IChangeEvent<FormData>) => {
    const { business_details, custom_claims: _customClaims, language, ...restFormData } = event.formData;

    const formData = {
      ...restFormData,
    } as FormData;

    const businessDetailsPayload = formToApiBusinessDetails(business_details);
    const businessDetailsForState = apiToFormBusinessDetails(businessDetailsPayload);
    delete formData.flights_enabled;
    delete formData.referral_program_enabled;
    delete formData.shadow_ban_user;
    delete formData.linked_personal_account;
    const shouldUpdateShadowBanUser =
      typeof this.state.shadowBanUser === 'boolean' && this.state.shadowBanUser !== this.props.user.shadow_ban_user;

    if (!formData.dob) {
      formData.dob = null;
    }

    if (formData.phone) {
      // replace all non-numeric characters with an empty string
      formData.phone = formData.phone.replace(/[^0-9]/g, '');
    }

    let user = formData as App.User;
    let userSaved = false;
    let businessDetailsSaved;
    try {
      user = await UsersService.updateUser(formData, this.props.brand);

      let toggleFlight: unknown = true;
      if (this.state.flightEnabled !== this.props.user.flights_enabled) {
        toggleFlight = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'flights_enabled',
          toggle_value: this.state.flightEnabled,
        });
      }

      let toggleReferralProgram: unknown = true;
      if (this.state.referralProgramEnabled !== this.props.user.referral_program_enabled) {
        toggleReferralProgram = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'referral_program_enabled',
          toggle_value: this.state.referralProgramEnabled,
        });
      }

      let toggleShadowBanUser;
      if (shouldUpdateShadowBanUser) {
        toggleShadowBanUser = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'shadow_ban_user',
          toggle_value: this.state.shadowBanUser,
        });
      }

      await UsersService.updateUserBusinessDetails(formData.id_member, businessDetailsPayload);
      if (language) {
        await UsersService.setCustomerLanguagePreference(formData.id_member, language, this.props.brand);
      } else {
        await UsersService.deleteCustomerLanguagePreference(formData.id_member, this.props.brand);
      }
      businessDetailsSaved = true;
      if (
        user &&
        toggleFlight &&
        toggleReferralProgram &&
        businessDetailsSaved &&
        (!shouldUpdateShadowBanUser || (shouldUpdateShadowBanUser && toggleShadowBanUser))
      ) {
        userSaved = true;
      }
    } catch (err) {
      const errorMessage = err.errors && err.errors.length ? err.errors[0] : err.message;
      this.setState({
        alertSuccess: false,
        alertError: true,
        errorMessage: errorMessage,
      });
    }

    if (userSaved && !isEqual(formData.partnerships, this.props.user.partnerships)) {
      try {
        await UsersService.updatePartnershipsDetails(formData.id_member, formData.partnerships);
      } catch (err) {
        const errorMessage = err.errors && err.errors.length ? err.errors[0] : err.message;
        this.setState({
          partnershipsAlertError: true,
          partnershipsErrorMessage: errorMessage,
        });
      }
    }

    if (userSaved) {
      user = await UsersService.getUser(formData.id_member, {
        brand: this.props.brand,
      });
      user.language = await UsersService.getCustomerLanguagePreference(formData.id_member, this.props.brand);
      const nextFormData: FormData = {
        ...cloneDeep(user),
        language: user.language ?? '',
        business_details: businessDetailsForState,
      };

      this.setState(
        {
          formData: nextFormData,
          alertSuccess: true,
          alertError: false,
          errorMessage: '',
        },
        () => {
          if (this.props?.update) {
            this.props.update(user);
          }
          if (this.props.onBusinessDetailsSaved) {
            this.props.onBusinessDetailsSaved();
          }
          //this.props.update causes handleChange to fire, which resets alertSuccess
          this.setState({
            alertSuccess: true,
          });
        },
      );
    }
  };

  nativeCurrencyCode() {
    const region = libRegions.getRegionByCode(this.state.formData.country_code);
    return region ? region.currencyCode : 'Currency is not available as country is not set';
  }

  render() {
    const alertError = (
      <Alert severity="error">
        <strong>Error!</strong> Unable to save user. {this.state.errorMessage}.
      </Alert>
    );

    const alertSuccess = (
      <Alert severity="success">
        <strong>Success!</strong> User Information Saved
      </Alert>
    );

    const partnershipsAlertError = (
      <Alert severity="error">
        <strong>Error!</strong> Unable to save partnership details. {this.state.partnershipsErrorMessage}.
      </Alert>
    );

    const { schema, formData, uiSchema } = this.handleStateData();

    return (
      <Box sx={{ mt: 4 }}>
        {this.state.alertError && alertError}
        {this.state.alertSuccess && alertSuccess}
        {this.state.partnershipsAlertError && partnershipsAlertError}
        <Form
          formData={formData}
          schema={schema}
          uiSchema={uiSchema}
          templates={FORM_TEMPLATES}
          onSubmit={this.handleSubmit}
          onChange={this.handleChange}
          validator={validator}
          customValidate={this.customValidate}
          fields={customFields}
        >
          <dl>
            <dt className="h6 text-muted text-uppercase">Native Currency Code</dt>
            <dd>{this.nativeCurrencyCode()}</dd>
          </dl>

          <button
            ref={(btn) => {
              this.submitButton = btn;
            }}
            className="hidden"
          />
        </Form>
        {this.state.alertError && alertError}
        {this.state.alertSuccess && alertSuccess}
        {this.state.partnershipsAlertError && partnershipsAlertError}
      </Box>
    );
  }
}
